import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { LoaderController } from '@adornis/chemistry/controllers/loader-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { XInput } from '@adornis/forms/x-input.js';
import '@adornis/forms/x-radiogroup.js';
import type { XRadioGroup } from '@adornis/forms/x-radiogroup.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { getPredictedTokenUserInfo } from '@ils/slots/api/tokenUserInfo.js';
import { TokenUserInfo } from '@ils/slots/db/tokenUserInfo.js';
import { html } from 'lit';
import { repeat } from 'lit-html/directives/repeat.js';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { ref } from 'lit/directives/ref.js';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { catchError, filter, map, switchMap, throttleTime } from 'rxjs/operators';
import { ILSUser } from '../../db/ils-user.js';
import { fullscreen } from '../global-state.js';

enum tabs {
  EXAM = 'Exam Token',
  USER = 'User Token',
}

@customElement('ils-token')
export class ILSTokenLogin extends ChemistryLitElement {
  override styles() {
    return [{ ':host': { flex: '1' } }];
  }
  private examTokenInputElem: XInput | undefined;
  private userTokenInputElem: XInput | undefined;
  private roomInputElem: XRadioGroup | undefined;

  @state() public errorMessages = { examTab: '', userTab: '' };

  @state() examToken = new RXController(this, routingState.searchParams.get('exam'));

  @state() room = new RXController(
    this,
    routingState.searchParams.get('raum').pipe(
      filter(Boolean),
      map(room => parseInt(room)),
    ),
  );

  @state() private readonly _tokenUserInfo = new RXController(
    this,
    combineLatest([this.examToken.observable.pipe(), this.room.observable.pipe()]).pipe(
      filter((input: [Maybe<string>, Maybe<number>]): input is [string, number] => !!input[0] && !!input[1]),
      throttleTime(100),
      switchMap(([examToken, room]) => {
        return getPredictedTokenUserInfo(
          examToken,
          room,
        )(TokenUserInfo.allFields).catch(e => {
          this.handleExamTokenError(e);
        });
      }),
      catchError(e => {
        this.handleExamTokenError(e);

        return of(undefined);
      }),
    ),
  );

  @state() private selectedTab = 0;

  private handleExamTokenError(e: Error) {
    logger.error({ error: e }, 'Problem beim Predicten des Nutzers auf /token');
    void XDialog.alert(
      `In Raum ${this.room.value} findet aktuell keine Prüfung statt. Wenn dies doch der Fall ist nutzen Sie bitte den Nutzer-Token oder kontaktieren Sie den Admin.`,
    );
  }

  override render() {
    return html`
      <x-flex ${css({ width: '100%', height: '100%' })} center crossaxis-center>
        <x-flex padding="md" space="lg" ${css({ minWidth: '40%' })}>
          <x-h1> Prüfungslogin </x-h1>
          ${!this._tokenUserInfo.value ? this.examTokenView() : this.userTokenView()}
        </x-flex>
      </x-flex>
    `;
  }

  private userTokenView() {
    const userInfo = this._tokenUserInfo.value;
    return html`
      <x-flex space="md">
        <x-text> Bestätigen Sie bitte Ihre Angaben: </x-text>
        <x-basic-card padding="md" ${css({ width: '100%' })}>
          <x-flex space="md" ${css({ minWidth: '100' })}>
            <x-h3> ${userInfo?.fullName ?? 'nicht gefunden'} </x-h3>
            <x-text> Geboren am ${userInfo?.birthDay} </x-text>
            <x-text> Prüfungstag: ${userInfo?.examDurationInfo} </x-text>
            <x-button
              ${css({ marginLeft: 'auto', marginTop: this.spacing.sm })}
              @click=${() => {
                if (userInfo) void this.login(userInfo.shortToken);
              }}
            >
              Bestätigen
            </x-button>
          </x-flex>
        </x-basic-card>
      </x-flex>
    `;
  }

  private examTokenView() {
    return html`
      <x-basic-card ${css({ width: '600px', height: '300px', padding: '0' })}>
        <x-flex h100>
          <x-tabs .selected=${this.selectedTab}>
            ${repeat(
              Object.values(tabs),
              (tab, i) => html` <x-tab @click=${() => (this.selectedTab = i)}> ${tab} </x-tab> `,
            )}
          </x-tabs>
          ${this.selectedTab === 0 ? this.examTokenInput() : this.userTokenInput()}
        </x-flex>
      </x-basic-card>
    `;
  }

  private examTokenInput() {
    return html`
      <x-flex space="md" padding="md" ${css({ flexGrow: 1 })}>
        <x-text> Bitte geben Sie das Exam-Token an. </x-text>
        <x-flex space="md" ${css({ flexGrow: 1 })}>
          <x-input
            ${ref(e => (this.examTokenInputElem = e as XInput))}
            .value=${this.examToken.value}
            placeholder="Exam Token"
            type="string"
            @submit=${(e: ValueEvent<string>) => this.onExamTokenSubmit()}
          ></x-input>
          <x-radiogroup ${ref(e => (this.roomInputElem = e as XRadioGroup))}>
            <x-flex horizontal space="md">
              <x-radiobutton label="Raum 1" selectable="1"> </x-radiobutton>
              <x-radiobutton label="Raum 2" selectable="2"> </x-radiobutton>
              <x-radiobutton label="Raum 3" selectable="3"> </x-radiobutton>
              <x-radiobutton label="Raum 4" selectable="4"> </x-radiobutton>
              <x-radiobutton label="Fallback" selectable="5"> </x-radiobutton>
            </x-flex>
          </x-radiogroup>

          <x-text ${css({ color: this.colors.red })}>${this.errorMessages.examTab}</x-text>

          <x-button @click=${() => this.onExamTokenSubmit()} ${css({ marginLeft: 'auto', marginTop: 'auto' })}>
            Weiter
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async onExamTokenSubmit() {
    const examShortToken = (this.examTokenInputElem?.value.value as string | undefined)?.trim() ?? '';
    const room = parseInt(this.roomInputElem?.value.value);

    if (!examShortToken) {
      await XDialog.alert('Bitte geben Sie zuerst das Exam Token ein.');
    } else if (!room) {
      await XDialog.alert('Sie müssen einen Raum auswählen.');
    } else if (examShortToken && room) {
      goTo({
        path: '/token',
        searchParams: new URLSearchParams({ exam: examShortToken, raum: room.toString() }),
      });
    }
  }

  private userTokenInput() {
    return html`
      <x-flex space="md" padding="md" ${css({ flexGrow: 1 })}>
        <x-text> Bitte geben Sie das User-Token an. </x-text>
        <x-input
          ${ref(e => (this.userTokenInputElem = e as XInput))}
          placeholder="User Token"
          @submit=${() => this.onUserTokenSubmit()}
        ></x-input>
        <x-text ${css({ color: this.colors.red })}>${this.errorMessages.userTab}</x-text>

        <x-button @click=${() => this.onUserTokenSubmit()} ${css({ marginLeft: 'auto', marginTop: 'auto' })}>
          Einloggen
        </x-button>
      </x-flex>
    `;
  }

  private async onUserTokenSubmit() {
    const userToken = (this.userTokenInputElem?.value.value as string)?.trim();
    if (!userToken) {
      await XDialog.alert('Sie müssen zuerst ein User-Token eingeben.');
      return;
    }
    try {
      await this.login(userToken);
    } catch (e) {
      const error = e as Error;
      logger.error(error);
      await XDialog.alert(error.message);
    }
  }

  private async login(shortToken: string) {
    shortToken = shortToken.trim();

    if (!shortToken) {
      this.errorMessages.userTab = 'Bitte geben Sie ihren Token ein';
      this.requestUpdate();
      return;
    }

    if (shortToken.length !== 8) {
      this.errorMessages.userTab =
        'Der Token ist nicht lang genug oder zu lang. Bitte überprüfen Sie, dass Ihr Token exakt 8 Zeichen lang ist.';
      this.requestUpdate();
      return;
    }

    try {
      let token: any = undefined;

      const loginPromise = new Promise<boolean>(async (resolve, reject) => {
        try {
          token = await ILSUser.loginILS(shortToken);
          await firstValueFrom(currentUser<ILSUser>().pipe(filter(user => user?._id === token.userId)));
          resolve(true);
        } catch {
          resolve(false);
        }
      });

      void LoaderController.waitFor(loginPromise);

      const result = await loginPromise;

      if (!result) throw new Error('loginILS failed');

      fullscreen.next(true);

      goTo({ path: '/exam', searchParams: new URLSearchParams({ day: token.examDayID }) });
    } catch {
      const errorMsg = 'Login fehlgeschlagen. Bitte überprüfen Sie den Token auf Korrektheit.';
      this.errorMessages.userTab = errorMsg;
      logger.error(errorMsg);
      this.requestUpdate();
    }
  }
}
